import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { API_ENDPOINTS } from '../config';
import { trackEvent } from '../utils/analytics';
import {
  applyAdminViewAs,
  isSelfUseView,
  readAdminViewAs,
  writeAdminViewAs,
} from '../utils/adminViewAs';
import { clearTierCache, readTierCache, writeTierCache } from '../utils/tierCache';
import {
  clearActiveEntity,
  ENTITY_CHANGED_EVENT,
  getActiveEntitySlug,
  getEntityHeaders,
  setSelfUsePreview,
} from '../utils/activeEntity';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';

const AUTH_TOKEN_KEY = 'risklo_auth_token';
const ALLOWED_CONSISTENCY_PERCENTS = [20, 30, 40, 50, 60];
const TIER_FETCH_RETRY_DELAYS_MS = [0, 1500, 3000];

function normalizeRiskloUserData(userData) {
  const parsedPercent = Number(userData?.defaultConsistencyPercent);
  const defaultConsistencyPercent = ALLOWED_CONSISTENCY_PERCENTS.includes(parsedPercent) ? parsedPercent : 50;
  return {
    ...(userData || {}),
    defaultConsistencyPercent,
  };
}

/** First paint must match logged-in UI when session exists (avoid header Sign-in → profile jitter). */
function readInitialUserFromStorage() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('risklo_user');
    if (!raw) return null;
    return normalizeRiskloUserData(JSON.parse(raw));
  } catch (_err) {
    return null;
  }
}

function readInitialTierFromStorage(email) {
  const entitySlug = getActiveEntitySlug() || 'vector';
  const cached = readTierCache(email, entitySlug);
  if (!cached) {
    return {
      isPro: false,
      isMax: false,
      isVectorAdmin: false,
      isBeta: false,
      isDevMode: false,
      isAdmin: false,
      isFullAdmin: false,
      isVectorAdminPlus: false,
      isSupport: false,
      isSales: false,
      hasCache: false,
    };
  }
    return {
      isPro: cached.isPro,
      isMax: cached.isMax,
      isVectorAdmin: cached.isVectorAdmin,
      isBeta: cached.isBeta,
      isDevMode: cached.isDevMode,
      isAdmin: Boolean(cached.isFullAdmin),
      isFullAdmin: Boolean(cached.isFullAdmin),
      isVectorAdminPlus: Boolean(cached.isVectorAdminPlus),
      isSupport: Boolean(cached.isSupport),
      isSales: Boolean(cached.isSales),
      hasCache: true,
    };
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const initialUser = readInitialUserFromStorage();
  const initialTier = readInitialTierFromStorage(initialUser?.email);
  const [user, setUser] = useState(() => initialUser);
  const [isPro, setIsPro] = useState(() => initialTier.isPro);
  const [isMax, setIsMax] = useState(() => initialTier.isMax);
  const [isVectorAdmin, setIsVectorAdmin] = useState(() => initialTier.isVectorAdmin);
  const [isBeta, setIsBeta] = useState(() => initialTier.isBeta);
  const [isDevMode, setIsDevMode] = useState(() => initialTier.isDevMode);
  const [isAdmin, setIsAdmin] = useState(() => initialTier.isAdmin);
  const [isFullAdmin, setIsFullAdmin] = useState(() => initialTier.isFullAdmin);
  const [isVectorAdminPlus, setIsVectorAdminPlus] = useState(() => initialTier.isVectorAdminPlus);
  // Support team (SUPPORT_EMAILS): Max tier + every client in Trade Watcher.
  const [isSupport, setIsSupport] = useState(() => initialTier.isSupport);
  // Sales team (SALES_EMAILS): exactly Max, labelled Sales.
  const [isSales, setIsSales] = useState(() => initialTier.isSales);
  const [adminViewAs, setAdminViewAsState] = useState(() => readAdminViewAs(initialUser?.email));
  const [loading, setLoading] = useState(true);
  /** False until Pro / Max / Admin checks finish for the signed-in email (avoids Basic → Pro UI flash). */
  const [tierReady, setTierReady] = useState(() => {
    const email = String(initialUser?.email || '').trim();
    if (!email) return true;
    return initialTier.hasCache;
  });
  const [authError, setAuthError] = useState(null);

  const normalizeUserData = normalizeRiskloUserData;

  const persistUser = (userData, token) => {
    const normalizedUser = normalizeUserData(userData);
    setUser(normalizedUser);
    localStorage.setItem('risklo_user', JSON.stringify(normalizedUser));
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    }
  };

  const clearSession = () => {
    if (user?.email) clearTierCache(user.email);
    setUser(null);
    setIsPro(false);
    setIsMax(false);
    setIsVectorAdmin(false);
    setIsBeta(false);
    setIsDevMode(false);
    setIsAdmin(false);
    setIsFullAdmin(false);
    setIsVectorAdminPlus(false);
    setIsSupport(false);
    setIsSales(false);
    setAdminViewAsState('admin');
    setTierReady(true);
    localStorage.removeItem('risklo_user');
    localStorage.removeItem(AUTH_TOKEN_KEY);
    // The active brand belongs to the user, not the browser. Without this the
    // next person to sign in inherits the previous user's entity — see
    // clearActiveEntity for why that reads as an isolation leak.
    clearActiveEntity();
    try {
      sessionStorage.removeItem('risklo_root_dashboard_redirect');
    } catch (_err) {
      // ignore
    }
  };

  const persistTierSnapshot = useCallback((email, snapshot, entitySlug = null) => {
    if (!email) return;
    const slug = entitySlug || getActiveEntitySlug() || 'vector';
    writeTierCache(email, { ...snapshot, entitySlug: slug }, slug);
  }, []);

  const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    return fetch(url, { ...options, headers });
  };

  // Validate token / refresh user; user is already hydrated synchronously from risklo_user when present.
  useEffect(() => {
    const initializeAuth = async () => {
      const oauthParams = new URLSearchParams(window.location.search);
      if (oauthParams.get('signIn') === 'success') {
        // App.js OAuth handler will establish the session; skip validating a stale token.
        setLoading(false);
        return;
      }

      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const savedUser = localStorage.getItem('risklo_user');

      let activeEmail = user?.email ? String(user.email).trim() : '';
      if (!activeEmail && savedUser) {
        try {
          const normalizedUser = normalizeUserData(JSON.parse(savedUser));
          setUser(normalizedUser);
          activeEmail = normalizedUser.email ? String(normalizedUser.email).trim() : '';
        } catch (_err) {
          // Invalid JSON in localStorage, ignore
        }
      }

      if (token) {
        try {
          // 6s timeout so we don't block UI if /api/auth/me hangs (e.g. Whop API down)
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 6000);
          const response = await fetch(API_ENDPOINTS.authMe, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          const data = await response.json();
          if (response.ok && data?.success && data?.user?.email) {
            persistUser(data.user, token);
            setLoading(false);
            return;
          }
          // Only clear on 401 (invalid token / session). 403 = valid token but membership
          // check failed (e.g. Whop API timeout) - keep user logged in with cached data.
          if (response.status === 401) {
            clearSession();
          }
        } catch (_error) {
          // Timeout or network error: keep cached user from optimistic restore
        }
      }

      setLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check Pro status from backend. Returns tier snapshot when definitive, else null.
  const checkProStatus = async (email, entitySlug = null) => {
    if (!email) {
      setIsPro(false);
      setIsBeta(false);
      setIsDevMode(false);
      return { isPro: false, isBeta: false, isDevMode: false };
    }

    const slug = entitySlug || getActiveEntitySlug() || 'vector';

    try {
      const response = await fetch(API_ENDPOINTS.proStatus(email, slug), {
        headers: getEntityHeaders(),
      });
      if (response.status === 429) {
        console.warn('Rate limited while checking Pro status; keeping previous tier state.');
        return null;
      }
      if (response.status === 503) {
        console.warn('Pro status temporarily unavailable; keeping cached tier state.');
        return null;
      }
      if (!response.ok) {
        console.warn(`Pro status check failed (${response.status}); keeping previous tier state.`);
        return null;
      }
      const data = await response.json();
      if (data?.tierUnavailable) {
        console.warn('Pro status unavailable; keeping cached tier state.');
        return null;
      }
      const snapshot = {
        isPro: Boolean(data.isPro),
        isBeta: Boolean(data.isBeta),
        isDevMode: Boolean(data.devMode),
      };
      setIsPro(snapshot.isPro);
      setIsBeta(snapshot.isBeta);
      setIsDevMode(snapshot.isDevMode);
      return snapshot;
    } catch (error) {
      console.error('Error checking Pro status:', error);
      return null;
    }
  };

  // Check platform admin status (ADMIN_EMAILS only — not Vector Admin / Plus).
  // Staff tiers use isVectorAdmin / isVectorAdminPlus from max-status instead.
  const checkAdminStatus = async (entitySlug = null) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setIsAdmin(false);
      setIsFullAdmin(false);
      setIsVectorAdminPlus(false);
      setIsSupport(false);
      setIsSales(false);
      return { isAdmin: false, isFullAdmin: false };
    }
    try {
      const response = await fetch(API_ENDPOINTS.adminStatus, {
        headers: getBearerAuthHeaders(),
      });
      if (response.status === 429) {
        console.warn('Rate limited while checking Admin status; keeping previous tier state.');
        return null;
      }
      if (response.status === 401 || response.status === 403) {
        setIsAdmin(false);
        setIsFullAdmin(false);
        setIsVectorAdminPlus(false);
        setIsSupport(false);
        setIsSales(false);
        return { isAdmin: false, isFullAdmin: false };
      }
      if (response.status === 503) {
        console.warn('Admin status temporarily unavailable; keeping cached tier state.');
        return null;
      }
      if (!response.ok) {
        console.warn(`Admin status check failed (${response.status}); keeping previous tier state.`);
        return null;
      }
      const data = await response.json();
      const snapshot = {
        isAdmin: Boolean(data.isFullAdmin),
        isFullAdmin: Boolean(data.isFullAdmin),
      };
      setIsAdmin(snapshot.isAdmin);
      setIsFullAdmin(snapshot.isFullAdmin);
      return snapshot;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return null;
    }
  };

  // Check Max status from backend. Returns tier snapshot when definitive, else null.
  const checkMaxStatus = async (email, entitySlug = null) => {
    if (!email) {
      setIsMax(false);
      setIsVectorAdmin(false);
      setIsVectorAdminPlus(false);
      setIsSupport(false);
      setIsSales(false);
      return { isMax: false, isVectorAdmin: false, isVectorAdminPlus: false, isSupport: false, isSales: false };
    }

    const slug = entitySlug || getActiveEntitySlug() || 'vector';

    try {
      const response = await fetch(API_ENDPOINTS.maxStatus(email, slug), {
        headers: getEntityHeaders(),
      });
      if (response.status === 429) {
        console.warn('Rate limited while checking Max status; keeping previous tier state.');
        return null;
      }
      if (response.status === 503) {
        console.warn('Max status temporarily unavailable; keeping cached tier state.');
        return null;
      }
      if (!response.ok) {
        console.warn(`Max status check failed (${response.status}); keeping previous tier state.`);
        return null;
      }
      const data = await response.json();
      const snapshot = {
        isMax: Boolean(data.isMax),
        isVectorAdmin: Boolean(data.isVectorAdmin),
        isVectorAdminPlus: Boolean(data.isVectorAdminPlus),
        isSupport: Boolean(data.isSupport),
        isSales: Boolean(data.isSales),
        isDevMode: Boolean(data.isMax && data.devMode),
      };
      setIsMax(snapshot.isMax);
      setIsVectorAdmin(snapshot.isVectorAdmin);
      setIsVectorAdminPlus(snapshot.isVectorAdminPlus);
      setIsSupport(snapshot.isSupport);
      setIsSales(snapshot.isSales);
      if (snapshot.isDevMode) {
        setIsDevMode(true);
      }
      return snapshot;
    } catch (error) {
      console.error('Error checking Max status:', error);
      return null;
    }
  };

  const refreshTierFlags = async (email, { hadCachedTier = false, baseline = null, entitySlug = null } = {}) => {
    const normalizedEmail = String(email || '').trim();
    if (!normalizedEmail) return;

    const slug = entitySlug || getActiveEntitySlug() || 'vector';

    let proResult = null;
    let maxResult = null;
    let adminResult = null;

    for (let attempt = 0; attempt < TIER_FETCH_RETRY_DELAYS_MS.length; attempt += 1) {
      if (attempt > 0) {
        await sleep(TIER_FETCH_RETRY_DELAYS_MS[attempt]);
      }
      [proResult, maxResult, adminResult] = await Promise.all([
        checkProStatus(normalizedEmail, slug),
        checkMaxStatus(normalizedEmail, slug),
        checkAdminStatus(slug),
      ]);
      if (proResult && maxResult && adminResult) break;
    }

    const merged = {
      isPro: proResult?.isPro ?? baseline?.isPro ?? false,
      isMax: maxResult?.isMax ?? baseline?.isMax ?? false,
      isVectorAdmin: maxResult?.isVectorAdmin ?? baseline?.isVectorAdmin ?? false,
      isBeta: proResult?.isBeta ?? baseline?.isBeta ?? false,
      isDevMode: proResult !== null || maxResult !== null
        ? Boolean(proResult?.isDevMode || maxResult?.isDevMode)
        : Boolean(baseline?.isDevMode),
      isAdmin: adminResult?.isAdmin ?? baseline?.isAdmin ?? false,
      isFullAdmin: adminResult?.isFullAdmin ?? baseline?.isFullAdmin ?? false,
      isVectorAdminPlus: maxResult?.isVectorAdminPlus ?? baseline?.isVectorAdminPlus ?? false,
      isSupport: maxResult?.isSupport ?? baseline?.isSupport ?? false,
      isSales: maxResult?.isSales ?? baseline?.isSales ?? false,
      entitySlug: slug,
    };

    if (proResult || maxResult || adminResult) {
      persistTierSnapshot(normalizedEmail, merged, slug);
      return;
    }

    if (hadCachedTier) {
      console.warn('Tier refresh failed after retries; keeping cached tier state.');
      return;
    }

    console.warn('Tier refresh failed after retries with no cached tier.');
  };

  // Refresh Pro/Max/Admin tier flags when the active brand changes.
  useEffect(() => {
    const email = user?.email ? String(user.email).trim() : '';
    if (!email) return undefined;

    const onEntityChanged = () => {
      const entitySlug = getActiveEntitySlug() || 'vector';
      const cachedTier = readTierCache(email, entitySlug);
      if (cachedTier) {
        setIsPro(cachedTier.isPro);
        setIsMax(cachedTier.isMax);
        setIsVectorAdmin(cachedTier.isVectorAdmin);
        setIsBeta(cachedTier.isBeta);
        setIsDevMode(cachedTier.isDevMode);
        setIsAdmin(cachedTier.isAdmin);
        setIsFullAdmin(cachedTier.isFullAdmin);
        setIsVectorAdminPlus(cachedTier.isVectorAdminPlus);
        setIsSupport(cachedTier.isSupport);
        setIsSales(cachedTier.isSales);
        setTierReady(true);
      } else {
        setTierReady(false);
      }
      refreshTierFlags(email, {
        hadCachedTier: Boolean(cachedTier),
        baseline: cachedTier,
        entitySlug,
      }).finally(() => setTierReady(true));
    };

    window.addEventListener(ENTITY_CHANGED_EVENT, onEntityChanged);
    return () => window.removeEventListener(ENTITY_CHANGED_EVENT, onEntityChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // One coordinated tier fetch per signed-in email (Pro / Max / Admin in parallel).
  useEffect(() => {
    let cancelled = false;
    const email = user?.email ? String(user.email).trim() : '';
    if (!email) {
      setIsPro(false);
      setIsMax(false);
      setIsVectorAdmin(false);
      setIsBeta(false);
      setIsDevMode(false);
      setIsAdmin(false);
      setIsFullAdmin(false);
      setIsVectorAdminPlus(false);
      setIsSupport(false);
      setIsSales(false);
      setAdminViewAsState('admin');
      setTierReady(true);
      return () => {
        cancelled = true;
      };
    }

    setAdminViewAsState(readAdminViewAs(email));
    const entitySlug = getActiveEntitySlug() || 'vector';
    const cachedTier = readTierCache(email, entitySlug);
    if (cachedTier) {
      setIsPro(cachedTier.isPro);
      setIsMax(cachedTier.isMax);
      setIsVectorAdmin(cachedTier.isVectorAdmin);
      setIsBeta(cachedTier.isBeta);
      setIsDevMode(cachedTier.isDevMode);
      setIsAdmin(cachedTier.isAdmin);
      setIsFullAdmin(cachedTier.isFullAdmin);
      setIsVectorAdminPlus(cachedTier.isVectorAdminPlus);
      setIsSupport(cachedTier.isSupport);
      setIsSales(cachedTier.isSales);
      setTierReady(true);
    } else {
      setTierReady(false);
    }
    (async () => {
      try {
        await refreshTierFlags(email, {
          hadCachedTier: Boolean(cachedTier),
          baseline: cachedTier,
          entitySlug,
        });
      } finally {
        if (!cancelled) setTierReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // check* functions are stable in behavior; we only re-run when the session email changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Handle Google Sign-In
  // Accepts either:
  // 1. JWT credential string (from Google Identity Services)
  // 2. User info object (from combined OAuth flow)
  const handleGoogleSignIn = async (credentialOrUserInfo) => {
    try {
      // Case 1: combined OAuth callback already returned a server-signed RiskLo
      // token (email was Google-verified server-side). Trust and persist.
      if (typeof credentialOrUserInfo === 'object' && credentialOrUserInfo?.token && credentialOrUserInfo?.email) {
        const provider = credentialOrUserInfo.provider || 'google';
        persistUser({
          email: credentialOrUserInfo.email,
          name: credentialOrUserInfo.name || credentialOrUserInfo.email.split('@')[0],
          picture: credentialOrUserInfo.picture || null,
          provider,
        }, credentialOrUserInfo.token);
        trackEvent('login_success', { method: provider, source: 'oauth_callback' });
        setAuthError(null);
        return true;
      }

      if (typeof credentialOrUserInfo === 'object' && credentialOrUserInfo?.email && !credentialOrUserInfo?.credential) {
        throw new Error('Sign-in could not be completed. Please try again.');
      }

      // Case 2: a raw Google Identity Services credential (JWT id_token string).
      // Send it to the server for verification — never decode + trust locally.
      let credential = null;
      if (typeof credentialOrUserInfo === 'string') {
        try {
          const parsed = JSON.parse(credentialOrUserInfo);
          credential = parsed?.credential || null;
        } catch {
          // Not JSON: treat the whole string as the Google credential (id_token).
          credential = credentialOrUserInfo;
        }
      } else if (typeof credentialOrUserInfo === 'object') {
        credential = credentialOrUserInfo?.credential || null;
      }

      if (!credential) {
        throw new Error('Google sign-in could not be completed. Please try again.');
      }

      const response = await fetch(API_ENDPOINTS.authSocial, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success || !data?.token || !data?.user) {
        throw new Error(data?.error || 'Google sign-in failed');
      }
      persistUser(data.user, data.token);
      trackEvent('login_success', { method: 'google', source: 'social_login' });
      setAuthError(null);
      return true;
    } catch (error) {
      console.error('Google sign-in failed:', error);
      setAuthError(error.message || 'Failed to sign in. Please try again.');
      return false;
    }
  };

  const signInWithEmail = async (email, password) => {
    const response = await fetch(API_ENDPOINTS.authLogin, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success || !data?.token || !data?.user) {
      const error = new Error(data?.error || 'Failed to sign in');
      if (data?.code) error.code = data.code;
      if (data?.email) error.email = data.email;
      throw error;
    }
    persistUser(data.user, data.token);
    trackEvent('login_success', {
      method: 'email',
      source: 'email_password',
    });
    setAuthError(null);
    return data.user;
  };

  const registerWithEmail = async (name, email, password) => {
    const response = await fetch(API_ENDPOINTS.authRegister, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to create account');
    }
    setAuthError(null);
    if (data?.requiresEmailVerification) {
      return {
        requiresEmailVerification: true,
        email: data.email || email,
        message: data.message || 'Account created. Please verify your email before signing in. Check your spam folder if you don\'t see it.',
      };
    }
    if (data?.token && data?.user) {
      persistUser(data.user, data.token);
      trackEvent('signup_success', {
        method: 'email',
        auto_signed_in: true,
      });
      return data.user;
    }
    return data;
  };

  const verifyEmail = async (token) => {
    const response = await fetch(API_ENDPOINTS.authVerifyEmail(token));
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to verify email');
    }
    return data;
  };

  const resendVerificationEmail = async (email) => {
    const response = await fetch(API_ENDPOINTS.authResendVerification, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to resend verification email');
    }
    return data;
  };

  const forgotPassword = async (email) => {
    const response = await fetch(API_ENDPOINTS.authForgotPassword, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to send password reset email');
    }
    return data;
  };

  const resetPassword = async (token, password) => {
    const response = await fetch(API_ENDPOINTS.authResetPassword, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to reset password');
    }
    return data;
  };

  const signInWithMicrosoft = () => {
    const frontendUrl = window.location.origin;
    window.location.href = API_ENDPOINTS.authMicrosoftStart(frontendUrl);
  };

  const updateDefaultConsistencyPercent = async (defaultConsistencyPercent) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('Missing session');
    }
    const response = await fetch(API_ENDPOINTS.authDefaults, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ defaultConsistencyPercent }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success || !data?.user) {
      throw new Error(data?.error || 'Failed to update defaults');
    }
    persistUser(data.user);
    return data.user;
  };

  // Handle Sign Out
  const signOut = async () => {
    trackEvent('logout', { source: 'manual' });
    try {
      await fetchWithAuth(API_ENDPOINTS.authLogout, { method: 'POST' });
    } catch (_error) {
      // ignore
    }
    clearSession();
    // Reset the one-time root→Overview redirect so the next sign-in lands on
    // the Overview (Dashboard) again instead of the Blowout Risk home.
    try { sessionStorage.removeItem('risklo_root_dashboard_redirect'); } catch (_e) { /* ignore */ }
    // Clear auto-connect flag so user can re-authenticate if they sign in again
    if (user?.email) {
      sessionStorage.removeItem(`risklo_sheets_autoconnect_${user.email}`);
    }
  };

  // Refresh Pro status (call after successful payment)
  const refreshProStatus = () => {
    if (user?.email) {
      const entitySlug = getActiveEntitySlug() || 'vector';
      const cachedTier = readTierCache(user.email, entitySlug);
      refreshTierFlags(user.email, {
        hadCachedTier: Boolean(cachedTier),
        baseline: cachedTier,
        entitySlug,
      });
    }
  };

  /** Re-validate session + tier flags (used by global auto-refresh for long-lived tabs). */
  const refreshSession = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      const response = await fetch(API_ENDPOINTS.authMe, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const data = await response.json();
      if (response.ok && data?.success && data?.user?.email) {
        persistUser(data.user, token);
        const entitySlug = getActiveEntitySlug() || 'vector';
        const cachedTier = readTierCache(data.user.email, entitySlug);
        await refreshTierFlags(data.user.email, {
          hadCachedTier: Boolean(cachedTier),
          baseline: cachedTier,
          entitySlug,
        });
        return;
      }
      if (response.status === 401) clearSession();
    } catch (_error) {
      // Network/timeout: keep cached session.
    }
  };

  const isAdminActual = isFullAdmin;

  const effectiveTiers = useMemo(
    () => applyAdminViewAs(
      { isPro, isMax, isVectorAdmin, isVectorAdminPlus, isSupport, isSales, isBeta, isDevMode, isAdmin },
      adminViewAs,
      isAdminActual,
    ),
    [isPro, isMax, isVectorAdmin, isVectorAdminPlus, isSupport, isSales, isBeta, isDevMode, isAdmin, adminViewAs, isAdminActual],
  );

  const setAdminViewAs = useCallback((viewAs) => {
    if (!isAdminActual || !user?.email) return;
    writeAdminViewAs(user.email, viewAs);
    setAdminViewAsState(viewAs);
  }, [isAdminActual, user?.email]);

  const isAdminViewPreview = isAdminActual && adminViewAs !== 'admin';
  // Self-use Basic/Pro/Max: show the app as someone with no company.
  const isSelfUsePreview = isAdminActual && isSelfUseView(adminViewAs);
  useEffect(() => { setSelfUsePreview(isSelfUsePreview); }, [isSelfUsePreview]);

  const value = {
    user,
    isPro: effectiveTiers.isPro,
    isMax: effectiveTiers.isMax,
    isVectorAdmin: effectiveTiers.isVectorAdmin,
    isBeta: effectiveTiers.isBeta,
    isDevMode: effectiveTiers.isDevMode,
    isAdmin: effectiveTiers.isAdmin,
    isFullAdmin,
    isVectorAdminPlus: effectiveTiers.isVectorAdminPlus,
    isSupport: effectiveTiers.isSupport,
    isSales: effectiveTiers.isSales,
    isAdminActual,
    adminViewAs,
    setAdminViewAs,
    isAdminViewPreview,
    isSelfUsePreview,
    loading,
    tierReady,
    handleGoogleSignIn,
    signInWithEmail,
    registerWithEmail,
    verifyEmail,
    resendVerificationEmail,
    forgotPassword,
    resetPassword,
    signInWithMicrosoft,
    updateDefaultConsistencyPercent,
    signOut,
    refreshProStatus,
    refreshSession,
    authError,
    setAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

